import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, mediaUnitAttachments, mediaUnits } from '@ooh/contracts';
import { useApiClient } from '../../../plugs/api-client';
import { mediaUnitDetailQueryKey, mediaUnitListsQueryKey } from '../lib/query-keys';

const ServerMessageSchema = z.object({ message: z.string() });

function serverMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const parsed = ServerMessageSchema.safeParse(error.body);
  return parsed.success ? parsed.data.message : undefined;
}

export function useDeleteMediaUnit() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: number }>({
    mutationFn: ({ id }) => api.call(mediaUnits.deleteById, { id }),
    // DeleteMediaUnitDialog shows its own translated error on failure (including the
    // MEDIA_UNIT_REFERENCED_BY_DRAFT_OFFER conflict); this no-op keeps the app's global
    // mutation-error toast from also firing with the raw backend message.
    onError: () => undefined,
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: mediaUnitListsQueryKey() });
      queryClient.removeQueries({ queryKey: mediaUnitDetailQueryKey(id) });
    },
  });
}

export interface UploadAttachmentVars {
  mediaUnitId: string | number;
  file: File;
}

export interface UploadAttachmentError {
  type: 'server';
  message?: string;
}

export function useUploadAttachment() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, UploadAttachmentError, UploadAttachmentVars>({
    mutationFn: async ({ mediaUnitId, file }) => {
      try {
        await api.upload(mediaUnitAttachments.upload, file, {}, { id: mediaUnitId });
      } catch (error: unknown) {
        throw { type: 'server', message: serverMessage(error) } satisfies UploadAttachmentError;
      }
    },
    onSuccess: (_data, { mediaUnitId }) => {
      void queryClient.invalidateQueries({ queryKey: mediaUnitDetailQueryKey(mediaUnitId) });
    },
  });
}

export interface DeleteAttachmentVars {
  mediaUnitId: string | number;
  attachmentId: number;
}

export function useDeleteAttachment() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteAttachmentVars>({
    mutationFn: ({ mediaUnitId, attachmentId }) =>
      api.call(mediaUnitAttachments.delete, { id: Number(mediaUnitId), attachmentId }),
    onSuccess: (_data, { mediaUnitId }) => {
      void queryClient.invalidateQueries({ queryKey: mediaUnitDetailQueryKey(mediaUnitId) });
    },
  });
}
